import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Award, BookOpen, Clock, CheckCircle2, Circle } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [curriculum, setCurriculum] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([api.get("/progress"), api.get("/curriculum")]);
      setProgress(p.data);
      setCurriculum(c.data);
    })();
  }, []);

  if (!progress || !curriculum) {
    return <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 label-eyebrow">Loading your dashboard…</div>;
  }

  const completedModuleSet = new Set(progress.completed_module_ids);
  const completedLessonSet = new Set(progress.completed_lesson_ids);
  const nextModule = curriculum.modules.find((m) => !completedModuleSet.has(m.id)) || curriculum.modules[0];

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-12" data-testid="dashboard-page">
      {/* Greeting */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
        <div className="lg:col-span-7">
          <div className="label-eyebrow mb-3">Trainee · {user.email}</div>
          <h1 className="font-serif text-5xl text-ink leading-tight">
            Welcome back, <span className="italic">{user.name.split(" ")[0]}</span>.
          </h1>
          <p className="mt-4 text-muted-foreground max-w-[55ch] leading-relaxed">
            You are working toward North Carolina's 40-hour Certified Peer Support Specialist
            standard. Continue where you left off, or browse the full curriculum.
          </p>
        </div>
        <div className="lg:col-span-5 grid grid-cols-3 gap-0 border border-stone bg-white" data-testid="dashboard-stats">
          <Stat label="Hours logged" value={`${progress.hours_logged}`} subtle={`of ${progress.hours_required}`} />
          <Stat label="Modules" value={`${progress.modules_completed}`} subtle={`of ${progress.modules_total}`} divider />
          <Stat label="Progress" value={`${progress.percent_complete}%`} subtle={progress.certified ? "Certified" : "In progress"} divider />
        </div>
      </div>

      {/* Continue strip */}
      {nextModule && (
        <Link
          to={`/curriculum/${nextModule.id}`}
          className="block border border-stone bg-cream hover:bg-white transition-colors p-8 mb-12 group"
          data-testid="continue-cta"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="label-eyebrow mb-2">
                {completedModuleSet.has(nextModule.id) ? "Review" : "Continue"} · Module {nextModule.order} of {curriculum.module_count}
              </div>
              <h2 className="font-serif text-3xl text-ink">{nextModule.title}</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-[60ch]">{nextModule.summary}</p>
            </div>
            <ArrowRight size={28} strokeWidth={1.4} className="text-ink shrink-0 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      )}

      {/* Modules grid */}
      <div className="editorial-rule mb-8">The Curriculum · 12 modules · 40 hours</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-stone">
        {curriculum.modules.map((m) => {
          const done = completedModuleSet.has(m.id);
          const lessonsCompleted = m.lessons.filter((l) => completedLessonSet.has(l.id)).length;
          return (
            <Link
              key={m.id} to={`/curriculum/${m.id}`}
              className="border-r border-b border-stone p-7 bg-white hover:bg-cream transition-colors group block"
              data-testid={`module-card-${m.order}`}
            >
              <div className="flex items-start justify-between mb-4">
                <span className="label-eyebrow">Module {String(m.order).padStart(2, "0")} · {m.hours}h</span>
                {done ? <CheckCircle2 size={20} strokeWidth={1.4} className="text-sage" /> : <Circle size={20} strokeWidth={1.4} className="text-stone" />}
              </div>
              <h3 className="font-serif text-2xl text-ink leading-tight mb-3 group-hover:text-terracotta transition-colors">
                {m.title}
              </h3>
              <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{m.summary}</p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><BookOpen size={12} strokeWidth={1.5} />{m.lessons.length} lessons</span>
                <span className="flex items-center gap-1.5"><Clock size={12} strokeWidth={1.5} />{m.hours}h</span>
                <span>{lessonsCompleted}/{m.lessons.length} done</span>
              </div>
            </Link>
          );
        })}
      </div>

      {/* GH Employment Callout */}
      <Link
        to="/generational-health"
        className="block mt-12 group"
        data-testid="dashboard-gh-callout"
      >
        <div style={{ background: "#0B1F3A" }} className="text-white p-8 flex flex-col md:flex-row items-center justify-between gap-6 hover:opacity-95 transition-opacity">
          <div className="flex items-center gap-6">
            <img
              src="https://customer-assets.emergentagent.com/job_peer-support-elite/artifacts/uxvvui3f_gh%20logo.png"
              alt="Generational Health"
              className="w-20 h-20 object-contain shrink-0"
            />
            <div>
              <div className="text-[0.72rem] tracking-[0.22em] uppercase mb-1" style={{ color: "#16A5BD" }}>
                Employment Partner
              </div>
              <h3 className="font-serif text-2xl">Train here. Get hired at Generational Health.</h3>
              <p className="text-sm text-white/70 mt-1">A direct pipeline for Carolina Peer Institute trainees.</p>
            </div>
          </div>
          <ArrowRight size={28} strokeWidth={1.4} style={{ color: "#16A5BD" }} className="shrink-0 group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>

      {/* Certificate */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-0 border border-stone">
        <div className="p-8 bg-ink text-bone">
          <Award size={28} strokeWidth={1.4} className="text-terracotta mb-4" />
          <h3 className="font-serif text-3xl mb-3">Your certificate</h3>
          <p className="text-bone/80 leading-relaxed mb-6">
            Complete all twelve modules with a passing score of 80% or higher on each mastery
            quiz to earn your Certificate of Completion.
          </p>
          {progress.certified ? (
            <Link to="/certificate" className="btn-primary !bg-bone !text-ink hover:!bg-terracotta hover:!text-bone" data-testid="cert-view-link">
              View certificate
            </Link>
          ) : (
            <div className="text-sm text-bone/60">
              {progress.modules_completed} of {progress.modules_total} modules complete.
            </div>
          )}
        </div>
        <div className="p-8 bg-white">
          <h4 className="label-eyebrow mb-3">Recent activity</h4>
          {progress.recent_attempts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No quiz attempts yet. Begin a module to get started.</p>
          ) : (
            <ul className="space-y-3" data-testid="recent-attempts">
              {progress.recent_attempts.slice(0, 5).map((a, i) => (
                <li key={i} className="flex items-center justify-between text-sm border-b border-stone/60 pb-3 last:border-b-0">
                  <span className="text-ink">{a.module_id}</span>
                  <span className={`font-mono ${a.passed ? "text-sage" : "text-rust"}`}>{a.score}% {a.passed ? "✓" : "✗"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, subtle, divider }) {
  return (
    <div className={`p-5 ${divider ? "border-l border-stone" : ""}`}>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="font-serif text-3xl text-ink leading-none">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{subtle}</div>
    </div>
  );
}
